import { FC, useCallback, useMemo, useState } from 'react';
import { Box, Card, Collapse, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { SignedGroup } from './signatureFilters';
import SignatureDocRow from './SignatureDocRow';

interface SignedDocumentsSectionProps {
  signedGroups: Record<string, SignedGroup>;
}

const SignedDocumentsSection: FC<SignedDocumentsSectionProps> = ({
  signedGroups,
}) => {
  const groupKeys = Object.keys(signedGroups);
  const [expanded, setExpanded] = useState<string | false>(false);

  const sortedGroupEntries = useMemo(
    () =>
      groupKeys.map((key) => ({
        key,
        group: {
          ...signedGroups[key],
          docs: [...signedGroups[key].docs].sort((a, b) =>
            a.documentTitle.localeCompare(b.documentTitle)
          ),
        },
      })),
    [groupKeys, signedGroups]
  );

  const handleToggle = useCallback((key: string): void => {
    setExpanded((prev) => (prev === key ? false : key));
  }, []);

  if (groupKeys.length === 0) {
    return null;
  }

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
        backgroundColor: 'background.paper',
        p: 2,
      }}
    >
      {/* Section title */}
      <Typography
        variant="h6"
        fontWeight={700}
        color="text.primary"
        sx={{ mb: 2 }}
      >
        Signed
      </Typography>

      {/* Group entries */}
      <Stack gap={1.5}>
        {sortedGroupEntries.map(({ key, group }) => {
          const subtitle = [group.certCategory, group.effectiveYear]
            .filter(Boolean)
            .join(', ');
          const isOpen = expanded === key;

          return (
            <Box key={key}>
              {/* Expandable group header — grey pill */}
              <Box
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onClick={() => handleToggle(key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleToggle(key);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: 'background.neutral',
                  borderRadius: 1,
                  p: 2,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <Stack gap={0.5}>
                  {group.propertyName && (
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.primary"
                    >
                      {group.propertyName}
                    </Typography>
                  )}
                  {subtitle && (
                    <Typography variant="caption" color="text.secondary">
                      {subtitle}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {group.docs.length} document
                    {group.docs.length !== 1 ? 's' : ''}
                  </Typography>
                </Stack>
                {isOpen ? (
                  <ExpandLessIcon
                    sx={{ color: 'text.primary', fontSize: 24 }}
                  />
                ) : (
                  <ExpandMoreIcon
                    sx={{ color: 'text.primary', fontSize: 24 }}
                  />
                )}
              </Box>

              {/* Expanded document items */}
              <Collapse in={isOpen}>
                <Stack gap={1.5} sx={{ pt: 0 }}>
                  {group.docs.map((doc) => (
                    <SignatureDocRow key={doc.fsrId} doc={doc} mode="view" />
                  ))}
                </Stack>
              </Collapse>
            </Box>
          );
        })}
      </Stack>
    </Card>
  );
};

export default SignedDocumentsSection;
